import { Opportunity, OpportunityScore, Weights, ScoringParams } from '../types/opportunity';

const assignTierColors = (sortedOpportunities: OpportunityScore[]): OpportunityScore[] => {
  // Calculate medians for value and feasibility scores
  const valueScores = sortedOpportunities.map(opp => opp.valueScore);
  const feasibilityScores = sortedOpportunities.map(opp => opp.feasibilityScore);
  
  const valueMedian = calculateMedian(valueScores);
  const feasibilityMedian = calculateMedian(feasibilityScores);
  
  console.log('Medians calculated:', { valueMedian, feasibilityMedian });

  // Assign colors based on quadrants
  return sortedOpportunities.map(opp => ({
    ...opp,
    quadrantColor: determineQuadrantColor(opp, valueMedian, feasibilityMedian)
  }));
};

const calculateMedian = (numbers: number[]): number => {
  const sorted = [...numbers].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  
  return sorted[middle];
};

const determineQuadrantColor = (
  opp: OpportunityScore,
  valueMedian: number,
  feasibilityMedian: number
): string => {
  const isHighValue = opp.valueScore > valueMedian;
  const isHighFeasibility = opp.feasibilityScore > feasibilityMedian;

  if (isHighValue && isHighFeasibility) return "#CA0079"; // Top Right - Most Valuable + Most Feasible
  if (isHighValue && !isHighFeasibility) return "#3B50A2"; // Top Left - Most Valuable + Less Feasible
  if (!isHighValue && !isHighFeasibility) return "#F1AB11"; // Bottom Left - Less Valuable + Less Feasible
  return "#64748b"; // Bottom Right - Less Valuable + More Feasible
};

// Helper function to center vectors around the neutral baseline of 3
const centerVector = (vector: number[], baseline: number = 3): number[] => {
  return vector.map(val => val - baseline);
};

// Helper function to check if a vector is effectively all zeros
const isAllZeros = (vector: number[], tolerance: number = 0.001): boolean => {
  return vector.every(val => Math.abs(val) < tolerance);
};

// Demonstration function to show centering transformation
export const demonstrateCentering = (): void => {
  console.log('=== Centering Demonstration ===');
  
  // Example 1: Mixed priorities
  const mixedVector = [5, 1, 3, 4, 2, 3, 5];
  const centeredMixed = centerVector(mixedVector);
  console.log('Mixed priorities:', {
    original: mixedVector,
    centered: centeredMixed,
    interpretation: 'Shows deviations from neutral (3)'
  });
  
  // Example 2: All neutral (edge case)
  const neutralVector = [3, 3, 3, 3, 3, 3, 3];
  const centeredNeutral = centerVector(neutralVector);
  console.log('All neutral (edge case):', {
    original: neutralVector,
    centered: centeredNeutral,
    isAllZeros: isAllZeros(centeredNeutral),
    interpretation: 'All zeros after centering - will return 0 similarity'
  });
  
  // Example 3: High priorities
  const highVector = [5, 5, 5, 4, 4, 4, 5];
  const centeredHigh = centerVector(highVector);
  console.log('High priorities:', {
    original: highVector,
    centered: centeredHigh,
    interpretation: 'All positive deviations from neutral'
  });
  
  // Example 4: Low priorities
  const lowVector = [1, 1, 2, 1, 2, 1, 1];
  const centeredLow = centerVector(lowVector);
  console.log('Low priorities:', {
    original: lowVector,
    centered: centeredLow,
    interpretation: 'All negative deviations from neutral'
  });
  
  console.log('=== End Demonstration ===');
};

// Helper function to calculate cosine similarity between two vectors
// Now centers vectors around [3,3,3,3,3,3,3] baseline to measure deviations from neutral
const cosineSimilarity = (vectorA: number[], vectorB: number[]): number => {
  if (vectorA.length !== vectorB.length) {
    throw new Error('Vectors must have the same length');
  }
  
  // Center vectors around the neutral baseline of 3
  const baseline = 3;
  const centeredA = centerVector(vectorA, baseline);
  const centeredB = centerVector(vectorB, baseline);
  
  // Check if either vector becomes all zeros after centering (edge case where all inputs are 3)
  const isAllZerosA = isAllZeros(centeredA);
  const isAllZerosB = isAllZeros(centeredB);
  
  // If either vector is all zeros after centering, return 0 as a safe default
  if (isAllZerosA || isAllZerosB) {
    console.log('Edge case detected: vector becomes all zeros after centering, returning 0');
    return 0;
  }
  
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < centeredA.length; i++) {
    dotProduct += centeredA[i] * centeredB[i];
    normA += centeredA[i] * centeredA[i];
    normB += centeredB[i] * centeredB[i];
  }
  
  normA = Math.sqrt(normA);
  normB = Math.sqrt(normB);
  
  // Avoid division by zero (shouldn't happen now due to the all-zeros check above)
  if (normA === 0 || normB === 0) {
    return 0;
  }
  
  return dotProduct / (normA * normB);
};

export const calculateScores = (
  opportunities: Opportunity[], 
  weights: Weights,
  params: ScoringParams = {}
): OpportunityScore[] => {
  const { alpha = 0.5 } = params;
  console.log('Starting score calculations with weights:', weights, 'alpha:', alpha);
  console.log('Note: Vectors are now centered around [3,3,3,3,3,3,3] baseline to measure deviations from neutral priority');
  
  // Demonstrate the centering transformation
  demonstrateCentering();

  const valueDimensions = [
    'SpeedUpPermitting',
    'IncreaseStaffProductivity',
    'SimplifyApplicationExperience',
    'EnhanceEquitableAccessToServices',
    'StrengthenInternalVisibilityDecisionMaking',
    'IncreaseProcessTransparency',
    'BoostFinancialSustainability'
  ];

  const challengeFactors = [
    'CostAndEffort',
    'Risk',
    'Complexity'
  ];

  // First pass: calculate all feasibility cosine similarities to find min/max
  const feasibilityCosineSimilarities: number[] = [];
  
  opportunities.forEach(opp => {
    const challengeValues = challengeFactors.map(factor => 
      Number(opp[factor as keyof Opportunity]) || 0
    );
    const challengeWeights = challengeFactors.map(factor => 
      Number(weights[factor as keyof Weights]) || 0
    );
    
    // Invert challenge values (5 becomes 1, 1 becomes 5) to convert challenges to feasibility
    const feasibilityValues = challengeValues.map(value => 6 - value);
    
    // Log the centering transformation for debugging
    console.log(`Feasibility calculation for ${opp.Name}:`, {
      originalChallengeValues: challengeValues,
      invertedFeasibilityValues: feasibilityValues,
      challengeWeights: challengeWeights,
      centeredFeasibilityValues: centerVector(feasibilityValues),
      centeredChallengeWeights: centerVector(challengeWeights)
    });
    
    // Calculate cosine similarity for feasibility dimensions
    const feasibilityCosineSimilarity = cosineSimilarity(feasibilityValues, challengeWeights);
    feasibilityCosineSimilarities.push(feasibilityCosineSimilarity);
  });

  // Calculate dynamic min and max for feasibility
  const minFeasibilityCosine = Math.min(...feasibilityCosineSimilarities);
  const maxFeasibilityCosine = Math.max(...feasibilityCosineSimilarities);
  
  console.log('Feasibility cosine similarity range:', {
    min: minFeasibilityCosine.toFixed(3),
    max: maxFeasibilityCosine.toFixed(3),
    range: (maxFeasibilityCosine - minFeasibilityCosine).toFixed(3)
  });

  // First pass: calculate all value cosine similarities to find min/max
  const valueCosineSimilarities: number[] = [];
  
  opportunities.forEach(opp => {
    const opportunityValues = valueDimensions.map(dim => 
      Number(opp[dim as keyof Opportunity]) || 0
    );
    const valueWeights = valueDimensions.map(dim => 
      Number(weights[dim as keyof Weights]) || 0
    );
    
    // Log the centering transformation for debugging
    console.log(`Value calculation for ${opp.Name}:`, {
      opportunityValues: opportunityValues,
      valueWeights: valueWeights,
      centeredOpportunityValues: centerVector(opportunityValues),
      centeredValueWeights: centerVector(valueWeights)
    });
    
    // Calculate cosine similarity for value dimensions
    const valueCosineSimilarity = cosineSimilarity(opportunityValues, valueWeights);
    valueCosineSimilarities.push(valueCosineSimilarity);
  });

  // Calculate dynamic min and max for value
  const minValueCosine = Math.min(...valueCosineSimilarities);
  const maxValueCosine = Math.max(...valueCosineSimilarities);
  
  console.log('Value cosine similarity range:', {
    min: minValueCosine.toFixed(3),
    max: maxValueCosine.toFixed(3),
    range: (maxValueCosine - minValueCosine).toFixed(3)
  });

  let scoredOpportunities = opportunities.map((opp, index) => {
    // Use the pre-calculated value cosine similarity
    const valueCosineSimilarity = valueCosineSimilarities[index];
    
    // Map the dynamic range to 30-100%
    const valueScore = 0.30 + (0.70 * (valueCosineSimilarity - minValueCosine) / (maxValueCosine - minValueCosine));

    // Use the pre-calculated feasibility cosine similarity
    const feasibilityCosineSimilarity = feasibilityCosineSimilarities[index];
    
    // Map the dynamic range to 30-100%
    const feasibilityScore = 0.30 + (0.70 * (feasibilityCosineSimilarity - minFeasibilityCosine) / (maxFeasibilityCosine - minFeasibilityCosine));

    // Calculate final score using weighted geometric mean
    // FinalScore = Value^α × Feasibility^(1-α)
    // Add small epsilon to prevent mathematical edge cases at alpha = 0 or 1
    const epsilon = 0.001;
    const adjustedAlpha = Math.max(epsilon, Math.min(1 - epsilon, alpha));
    const finalScore = Math.pow(valueScore, adjustedAlpha) * Math.pow(feasibilityScore, 1 - adjustedAlpha);

    console.log(`Scored ${opp.Name} (BEFORE normalization):`, {
      valueScore: valueScore.toFixed(3),
      feasibilityScore: feasibilityScore.toFixed(3),
      finalScore: finalScore.toFixed(3),
      alpha: alpha.toFixed(2),
      'value%': (valueScore * 100).toFixed(1),
      'feasibility%': (feasibilityScore * 100).toFixed(1),
      'final%': (finalScore * 100).toFixed(1)
    });

    return {
      ...opp,
      valueScore,
      feasibilityScore,
      finalScore,
      quadrantColor: '' // Placeholder for tier color
    };
  });

  // Sort by final score and assign colors based on quadrants
  scoredOpportunities = assignTierColors(scoredOpportunities);

  return scoredOpportunities;
};